import { css } from "lit";
export const styles = css `
	:host {
		display: inline-flex;
		align-items: center;
		gap: var(--_gap);

		--_gap: var(--w-c-radio-gap, 8px);

		--_size: var(--w-c-radio-size, 2rem);
		--_radius: var(--w-c-radio-radius, 50%);
		--_border-width: var(--w-c-radio-border-width, 1px);
		--_checked-border-width: var(--w-c-radio-checked-border-width, 0.6rem);

		--_bg: var(--w-c-radio-bg, var(--w-s-color-background));
		--_border-color: var(
			--w-c-radio-border-color,
			var(--w-s-color-border-strong)
		);
		--_border-color-checked: var(
			--w-c-radio-border-color-checked,
			var(--w-s-color-border-selected)
		);
		--_border-color-invalid: var(
			--w-c-radio-border-color-invalid,
			var(--w-s-color-border-negative)
		);

		--_outline-width: var(--w-c-radio-outline-width, 2px);
		--_outline-color: var(
			--w-c-radio-outline-color,
			var(--w-s-color-border-focus)
		);
		--_outline-offset: var(
			--w-c-radio-outline-offset,
			var(--w-outline-offset, 1px)
		);

		--_border-color-disabled: var(
			--w-c-radio-border-color-disabled,
			var(--w-s-color-border-disabled)
		);
		--_bg-disabled: var(
			--w-c-radio-bg-disabled,
			var(--w-s-color-background-disabled-subtle)
		);

		--_label-font-size: var(--w-c-radio-label-font-size, var(--w-font-size-m));
		--_label-line-height: var(
			--w-c-radio-label-line-height,
			var(--w-line-height-m)
		);
		--_label-color: var(--w-c-radio-label-color, currentColor);
		--_label-color-disabled: var(
			--w-c-radio-label-color-disabled,
			var(--w-s-color-text-disabled)
		);

		--_cursor: var(--w-c-radio-cursor, pointer);
		--_cursor-disabled: var(--w-c-radio-cursor-disabled, not-allowed);

		--_transition: var(
			--w-c-radio-transition,
			border-color 150ms cubic-bezier(0.4, 0, 0.2, 1),
			border-width 150ms cubic-bezier(0.4, 0, 0.2, 1),
			background-color 150ms cubic-bezier(0.4, 0, 0.2, 1)
		);
	}

	:host([disabled]),
	:host([disabled-ui]) {
		--w-c-radio-border-color: var(
			--w-c-radio-border-color-disabled,
			var(--w-s-color-border-disabled)
		);
		--w-c-radio-bg: var(
			--w-c-radio-bg-disabled,
			var(--w-s-color-background-disabled-subtle)
		);
		--w-c-radio-border-color-checked: var(
			--w-c-radio-border-color-disabled,
			var(--w-s-color-border-disabled)
		);
		--w-c-radio-label-color: var(
			--w-c-radio-label-color-disabled,
			var(--w-s-color-text-disabled)
		);
		--w-c-radio-cursor: var(--w-c-radio-cursor-disabled, not-allowed);
	}

	[part="base"] {
		display: inline-flex;
		align-items: center;
		gap: var(--_gap);
	}

	[part="control"] {
		appearance: none;
		-webkit-appearance: none;
		margin: 0;

		width: var(--_size);
		height: var(--_size);
		border-style: solid;
		border-width: var(--_border-width);
		border-color: var(--_border-color);
		border-radius: var(--_radius);
		background-color: var(--_bg);
		cursor: var(--_cursor);
		flex-shrink: 0;

		transition: var(--_transition);
	}

	:host([checked]) [part="control"],
	:host([checked-ui]) [part="control"],
	:host([role="radio"][checked-ui]) [part="control"],
	:host([role="radio"]:state(checked)) [part="control"] {
		border-color: var(--_border-color-checked);
		border-width: var(--_checked-border-width);
	}

	:host([invalid]) [part="control"] {
		border-color: var(--_border-color-invalid);
	}

	:host(:focus-visible) {
		outline: none;
	}

	:host(:focus-visible) [part="control"] {
		outline: var(--_outline-width) solid var(--_outline-color);
		outline-offset: var(--_outline-offset);
	}

	:host([disabled]) [part="control"],
	:host([disabled-ui]) [part="control"],
	:host([role="radio"]:state(disabled)) [part="control"] {
		border-color: var(--_border-color-disabled);
		background-color: var(--_bg-disabled);
		cursor: var(--_cursor-disabled);
	}

	:host([disabled]) [part="label"],
	:host([disabled-ui]) [part="label"] {
		color: var(--_label-color-disabled);
		cursor: var(--_cursor-disabled);
	}

	:host([disabled][checked]),
	:host([disabled][checked-ui]),
	:host([disabled-ui][checked-ui]) [part="control"] {
		border-color: var(--_border-color-disabled);
	}

	[part="label"] {
		font-size: var(--_label-font-size);
		line-height: var(--_label-line-height);
		color: var(--_label-color);
		user-select: none;
		cursor: var(--_cursor);
	}

	@media (prefers-reduced-motion: reduce) {
		:host {
			--w-c-radio-transition: none;
		}
	}

	@media (forced-colors: active) {
		:host {
			--w-c-radio-bg: Canvas;
			--w-c-radio-border-color: ButtonText;
			--w-c-radio-border-color-checked: Highlight;
			--w-c-radio-outline-color: Highlight;
			--w-c-radio-bg-disabled: Canvas;
			--w-c-radio-border-color-disabled: GrayText;
			--w-c-radio-label-color: CanvasText;
			--w-c-radio-label-color-disabled: GrayText;
		}

		[part="control"] {
			forced-color-adjust: none;
		}

		:host([checked]) [part="control"],
		:host([checked-ui]) [part="control"],
		:host([role="radio"]:state(checked)) [part="control"] {
			background-color: var(--_border-color-checked);
			border-color: var(--_border-color-checked);
		}

		:host([disabled][checked]),
		:host([disabled][checked-ui]),
		:host([disabled-ui][checked-ui]) [part="control"],
		:host([role="radio"]:state(disabled):state(checked)) [part="control"] {
			background-color: var(--_border-color-disabled);
			border-color: var(--_border-color-disabled);
		}
	}
`;
//# sourceMappingURL=radio-styles.js.map